import {
  Typography,
  Box,
  Divider,
  Stack,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  InputAdornment,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Link,
} from "@mui/material";
import ManualIcon from "src/assets/svg-icons/manual.svg";
import RecipeIcon from "src/assets/svg-icons/recipe.svg";
import BoxSelect from "src/shared/components/box-select";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormWatch,
} from "react-hook-form";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { COGSTypes, FormModeTypes } from "src/types/generic";
import { FormData } from "../../constants";
import YoutubeViewerBox from "../../youtube-viewer-box";
import { useTranslation } from "react-i18next";
import { ICOGS } from "src/types/add-sales";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { formatNumber } from "src/utils";
import InfoBox from "src/shared/components/info-box";

const CalculateCOGS = ({
  errors,
  control,
  watch,
  COGSType,
  setCOGSType,
  journeyData,
  journeyTaxDivider,
  lastCogs,
  mode,
}: {
  errors: FieldErrors<FormData>;
  control: Control<FormData, any>;
  watch: UseFormWatch<FormData>;
  COGSType: COGSTypes;
  setCOGSType: Function;
  journeyData: any;
  journeyTaxDivider: number;
  lastCogs?: ICOGS;
  mode: FormModeTypes;
}) => {
  const { t, i18n } = useTranslation("addSales");
  const { t: generalT } = useTranslation("general");

  const notMatchFoodics =
    mode === "add" &&
    watch("journey_type") === "foodics" &&
    !journeyData?.sale?.match_foodics;

  const netSalesWithoutTax =
    watch("journey_type") === "foodics"
      ? watch("has_jazr_calculations") === "no"
        ? journeyData?.sale?.foodics_net_sales
        : journeyData?.sale.gross_sales / journeyTaxDivider
      : watch("journey_type") === "excel"
      ? journeyData?.sale.gross_sales / journeyTaxDivider
      : +watch("net_sales_with_tax") / journeyTaxDivider;

  return (
    <div>
      <Typography fontSize={"26px"} fontWeight={600}>
        {t("letFindCostSales")}
      </Typography>
      <Typography color={"textSecondary"} mb={4}>
        {t("fillTheRequiredInfo")}
      </Typography>

      <YoutubeViewerBox videoId="djvKcUwSmqw" />

      {(((watch("journey_type") === "foodics" ||
        watch("journey_type") === "excel") &&
        watch("branchHasMenuFeature")) ||
        (watch("journey_type") === "manual" && watch("productSales"))) && (
        <BoxSelect
          label={t("howAddCogs")}
          options={[
            {
              label: t("manually"),
              value: "manual",
              icon: <img src={ManualIcon} alt="" />,
            },
            {
              label: t("recipeCost"),
              value: "recipe",
              icon: <img src={RecipeIcon} alt="" />,
              disabled:
                (watch("journey_type") === "manual" &&
                  !watch("productSales")) ||
                !watch("branchHasMenuFeature"),
            },
          ]}
          value={COGSType}
          onChange={(value) => setCOGSType(value)}
        />
      )}
      {COGSType === "recipe" ? (
        <>
          <Divider variant="fullWidth" sx={{ mt: 3, mb: 6 }} />
          <Typography
            color={"#475467"}
            fontSize={"18px"}
            textAlign={"center"}
            width={"100%"}
          >
            {t("recipeCost")}: {journeyData?.cogs?.recipe_amount ?? 0}
          </Typography>
        </>
      ) : (
        <Box mt={2}>
          {lastCogs ? (
            <Stack mb={2}>
              <InfoBox
                variant="info"
                text={`${t("lastAddedSales")} ${
                  lastCogs?.start_date
                } ${generalT("to")}
                ${lastCogs?.end_date} (COGS : ${lastCogs?.percentage}%)`}
              />
            </Stack>
          ) : null}

          <Stack
            direction={"row"}
            alignItems={"center"}
            gap={{ xs: 1, sm: 1, md: 3 }}
            flexWrap={"wrap"}
            width={"100%"}
            mb={1.5}
          >
            <Typography color={"textSecondary"}>{t("typeValue")}</Typography>
            <FormControl
              fullWidth
              size="small"
              error={!!errors.has_vat}
              sx={{ width: "fit-content", ml: { xs: "auto", sm: "unset" } }}
              disabled={journeyData?.cogs?.type === "formula"}
            >
              <Controller
                name="COGS_manual_type"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    {...field}
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                  >
                    <FormControlLabel
                      value="percentage"
                      control={
                        <Radio
                          icon={<CircleOutlinedIcon />}
                          checkedIcon={<CheckCircleIcon />}
                        />
                      }
                      label={t("percentage")}
                      sx={{ color: "#98A2B3" }}
                    />
                    <FormControlLabel
                      value="amount"
                      control={
                        <Radio
                          icon={<CircleOutlinedIcon />}
                          checkedIcon={<CheckCircleIcon />}
                        />
                      }
                      label={generalT("amount")}
                      sx={{ color: "#98A2B3" }}
                    />
                  </RadioGroup>
                )}
              />
            </FormControl>
          </Stack>
          <Stack direction={"row"} justifyContent={"space-between"}>
            <Controller
              name="COGS_value"
              control={control}
              render={({ field }) => (
                <TextField
                  sx={{ mb: 3, maxWidth: { xs: "200px", sm: "313px" } }}
                  required
                  label={t("cogsValue")}
                  {...field}
                  error={!!errors.COGS_value}
                  helperText={errors.COGS_value?.message}
                  disabled={journeyData?.cogs?.type === "formula"}
                  fullWidth
                  size="small"
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {watch("COGS_manual_type") === "percentage"
                          ? "%"
                          : "SAR"}
                      </InputAdornment>
                    ),
                    disableUnderline: true,
                    inputProps: {
                      max:
                        watch("COGS_manual_type") === "percentage" ? "100" : "",
                    },
                  }}
                />
              )}
            />
            <Typography color={"#667085"}>
              {watch("COGS_manual_type") === "percentage"
                ? generalT("amount")
                : t("percentage")}
              :{" "}
              <Typography component={"span"} fontWeight={800}>
                {watch("COGS_value") !== undefined
                  ? watch("COGS_manual_type") === "percentage"
                    ? formatNumber(
                        +(
                          (watch("COGS_value") / 100) *
                          netSalesWithoutTax
                        ).toFixed(2)
                      )
                    : (
                        (watch("COGS_value") / netSalesWithoutTax) *
                        100
                      ).toFixed(2)
                  : "0"}{" "}
                {watch("COGS_manual_type") === "percentage"
                  ? generalT("sar")
                  : "%"}
              </Typography>
            </Typography>
          </Stack>
          <Accordion variant="elevation" elevation={1}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              <Typography color={"textSecondary"}>
                {t("stillNotCountInventory")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography fontSize={"14px"}>
                {t("doNotWorryAddClosestCOGS")}
              </Typography>
            </AccordionDetails>
          </Accordion>
          {notMatchFoodics && (
            <Stack
              mb={1}
              mt={2.5}
              width={"100%"}
              bgcolor={"#FFF3EA"}
              borderRadius={"4px"}
              paddingX={2}
              paddingY={1}
              gap={"10px"}
              border={"1px solid #FF8734"}
              direction={"row"}
            >
              <InfoOutlinedIcon color="primary" />
              <Stack>
                <Typography fontSize={"14px"}>
                  {t("taxAtFoodics")} {journeyData.sale.foodics_net_sales_tax} ({" "}
                  {journeyData.sale.foodics_tax_percentage}% ){" "}
                  {t("AndShouldBe")}{" "}
                  <Link
                    color={"secondary"}
                    href="https://www.youtube.com/watch?v=MWy22WA1AsE"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                  >
                    See a video
                  </Link>
                </Typography>
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  gap={{ xs: 1, sm: 1, md: 3 }}
                  flexWrap={"wrap"}
                  width={"100%"}
                >
                  <Typography fontSize={"14px"}>
                    {t("doWantCalculation")}
                    {i18n.language === "en" ? "?" : "؟"}
                  </Typography>
                  <FormControl
                    fullWidth
                    size="small"
                    sx={{
                      width: "fit-content",
                      ml: { xs: "auto", sm: "unset" },
                    }}
                  >
                    <Controller
                      name="has_jazr_calculations"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup
                          {...field}
                          key={field.value}
                          row
                          aria-labelledby="demo-row-radio-buttons-group-label"
                          name="row-radio-buttons-group"
                        >
                          <FormControlLabel
                            value="yes"
                            control={
                              <Radio
                                icon={<CircleOutlinedIcon />}
                                checkedIcon={<CheckCircleIcon />}
                              />
                            }
                            label={generalT("yes")}
                            sx={{ color: "#98A2B3" }}
                          />
                          <FormControlLabel
                            value="no"
                            control={
                              <Radio
                                icon={<CircleOutlinedIcon />}
                                checkedIcon={<CheckCircleIcon />}
                              />
                            }
                            label={generalT("no")}
                            sx={{ color: "#98A2B3" }}
                          />
                        </RadioGroup>
                      )}
                    />
                  </FormControl>
                </Stack>
              </Stack>
            </Stack>
          )}
        </Box>
      )}
    </div>
  );
};

export default CalculateCOGS;
